import {
  OperationType,
  ROUTE_PATH,
  SORTING_ORDER,
} from 'common/constants/sm-constants';
import { MetaModel } from 'common/models/utility-models/base-model';
import {
  CommonRowProperties,
  additionalActions,
  cardItem,
  deleteConstraint,
  detailCTA,
  excelDownload,
  extraHeaderActions,
  filterCheckboxModel,
  initCheckboxDetails,
  multipleChoiceOption,
  navInfo,
  preview,
  statusActions,
  switchLabel,
  toggleInfo,
} from './utility-interface';

export abstract class SmMetaModel extends MetaModel {
  override properties: any;
  constructor(
    title: string,
    redirectPath: string,
    isStepForm: boolean = false
  ) {
    super();
    this.pageTitle = title;
    this.redirectPath = redirectPath;
    this.isStepForm = isStepForm;
  }

  abstract getMetaModel(): MetaModel;
  abstract initProperties(): any;
}

export abstract class SmChartsModel extends SmMetaModel {
  constructor(
    protected pageName: string,
    public override redirectPath: string,
    public redirectionUrl: string,
    protected numberOfRows: number,
    protected errorMessage: string = null
  ) {
    super(pageName, redirectPath);
  }

  getMetaModel(): MetaModel {
    const pageInfo = new MetaModel();
    pageInfo.pageTitle = this.pageTitle;
    pageInfo.properties = this.initProperties();
    pageInfo.redirectPath = this.redirectPath;
    return pageInfo;
  }

  initProperties() {
    const props = {
      chartName: '',
      chartOptions: '',
    };
    return props;
  }
}

export abstract class GridMetaModel<T> extends SmMetaModel {
  constructor(
    name: string,
    pagePath: string,
    public override redirectPath: string,
    protected isEditable: boolean,
    protected isDeletable: boolean,
    protected recordPerPage: number,
    protected hasRedirection: boolean = false,
    protected isAdd: boolean = true,
    protected searchableFields: string[] = [],
    protected isExtraActions: boolean = true,
    protected actionKeys: additionalActions[] = [],
    protected downloadExcel: excelDownload = {
      isDownloadExcel: false,
      excelFileName: '',
      btnLabel: '',
      headers: [],
    },
    protected isDateFilter: boolean = false,
    protected EditCondition: any = null,
    protected isStatusFilter: boolean = false,
    protected isSubTable: boolean = false,
    protected isSelectAll: boolean = false,
    public redirectTo: string = null,
    protected showDeleteIcon: deleteConstraint = null,
    protected isCreateAlternate: boolean = false,
    protected isDetail: string = null,
    protected preview: preview = {
      isPreview: false,
      isPreviewType: 'VIDEO',
      isEnvUrl: false,
      linkKey: ['filePath'],
      previewName: '',
    },
    protected tabSwitch: switchLabel = {
      isTabSwitch: false,
      label: '',
      isChecked: false,
    },
    protected isSearch: boolean = true,
    protected showFooter: boolean = true,
    protected isCommonHeader: boolean = false,
    protected commonRowProps: CommonRowProperties[] = [],
    protected showHeader: boolean = true,
    protected extraHeaderActions: extraHeaderActions = {
      isExtraActions: false,
      btnLabel: '',
      selectOptions: [],
      selectedValue: '',
      isPreviewBtn:false,
      previewBtnLabel:'',
      count:false
    },
    protected checkboxEmitIds: initCheckboxDetails = {
      isCheckboxEmit: false,
      emitKey: 'id',
      checkedKey: '',
      valueKey: '',
    },
    protected downloadDetails: any = {
      isDownload: false,
      key: '',
      valueKey: '',
    },
    protected statusActions: statusActions = {
      isTabStatus: false,
      label: '',
      isChecked: false,
    },
    protected totalEntry: boolean = false,
    protected showDeleteButton: boolean = true,
    protected deleteButtonCss: {
      [key: string]: boolean;
    } = {
      isPrimary: true,
      isDanger: false,
    },
    protected totalTag: string = '',
    protected subHeader: {
      value: string;
      isVisible: boolean;
    } = { value: '', isVisible: false },
    protected noPlaceHolderRequired: boolean = false,
    protected changeDeleteAllPayload:boolean = false,
    public isHighlight: boolean = true ,
    protected extraRowAction:  extraHeaderActions = {
      isExtraActions: false,
      btnLabel: '',
      selectOptions: [],
      selectedValue: '',
      isPreviewBtn:false,
      previewBtnLabel:'',
    },
    public isAddLabel: {
      value: string;
      isVisible: boolean}= { value: '', isVisible: false },
    public isImportExcel: boolean = false,
    public extraActionConstraint: deleteConstraint = null,
    protected extraAction: extraHeaderActions = {
      isExtraActions: false,
      btnLabel: '',
      selectOptions: [],
      selectedValue: '',
      isPreviewBtn:false,
      previewBtnLabel:'',
    },
    protected isHover:boolean = false,
  ) {
    super(name, pagePath);
  }

  abstract prepareHeaderList(): T[];

  getMetaModel(): MetaModel {
    const pageData = new MetaModel();
    pageData.pageTitle = this.pageTitle;
    pageData.properties = this.initProperties();
    pageData.redirectPath = this.redirectPath;
    return pageData;
  }

  initProperties(): any {
    const dataInfo = {
      isEditable: this.isEditable,
      isDeletable: this.isDeletable,
      recordPerPage: this.recordPerPage,
      hasRedirection: this.hasRedirection,
      isAdd: this.isAdd,
      searchableFields: this.searchableFields,
      isExtraActions: this.isExtraActions,
      actionAttributes: this.actionKeys,
      DownloadExcel: this.downloadExcel,
      isDateFilter: this.isDateFilter,
      EditCondition: this.EditCondition,
      isStatusFilter: this.isStatusFilter,
      isSubTable: this.isSubTable,
      isSelectAll: this.isSelectAll,
      redirectTo: this.redirectTo,
      showDeleteIcon: this.showDeleteIcon,
      detailPath: this.isDetail,
      preview: this.preview,
      tabSwitch: this.tabSwitch,
      isSearch: this.isSearch,
      showFooter: this.showFooter,
      isCommonHeader: this.isCommonHeader,
      commonRowProps: this.commonRowProps,
      showHeader: this.showHeader,
      extraHeaderActions: this.extraHeaderActions,
      checkboxEmitIds: this.checkboxEmitIds,
      downloadDetails: this.downloadDetails,
      totalEntry: this.totalEntry,
      deleteButtonCss: this.deleteButtonCss,
      totalTag: this.totalTag,
      subHeader: this.subHeader,
      gridInfo: this.prepareHeaderList(),
      showDeleteButton: this.showDeleteButton,
      noPlaceHolderRequired: this.noPlaceHolderRequired,
      changeDeleteAllPayload:this.changeDeleteAllPayload,
      isHighlight: this.isHighlight,
      extraRowAction: this.extraRowAction,
      isAddLabel:this.isAddLabel,
      isImportExcel: this.isImportExcel,
      extraActionConstraint: this.extraActionConstraint,
      extraAction: this.extraAction,
      isHover: this.isHover
    };
    return dataInfo;
  }
}

export abstract class DetailMetaModel<T> extends SmMetaModel {
  constructor(
    name: string,
    pagePath: string,
    protected numberOfRows: number,
    protected ctaName: string = null,
    protected ctaData: detailCTA = null,
    protected isGrid: boolean = false,
    protected gridMetaModel: any = null,
    protected headers: string[] = []
  ) {
    super(name, pagePath);
  }
  abstract prepareParamList(): T[];

  override getMetaModel(): MetaModel {
    const pageMeta = new MetaModel();
    pageMeta.pageTitle = this.pageTitle;
    pageMeta.properties = this.initProperties();
    pageMeta.redirectPath = this.redirectPath;
    return pageMeta;
  }

  initProperties(): any {
    const dataInfo = {
      numberOfRows: this.numberOfRows,
      ctaName: this.ctaName,
      ctaData: this.ctaData,
      detailInfo: this.prepareParamList(),
      isGridComponent: this.isGrid,
      gridMetaModel: this.gridMetaModel,
    };
    return dataInfo;
  }
}

export abstract class cardItemMetaModel extends SmMetaModel {
  id?: number;
  cardItem?: cardItem[] = [];

  constructor(name: string, pagePath: string, protected propcardItem: number) {
    super(name, pagePath);
  }

  override getMetaModel(): MetaModel {
    const pageMeta = new MetaModel();
    pageMeta.pageTitle = this.pageTitle;
    pageMeta.properties = this.initProperties();
    pageMeta.redirectPath = this.redirectPath;
    return pageMeta;
  }

  initProperties(): any {
    const dataInfo = {
      cardItems: this.cardItem,
    };
    return dataInfo;
  }
}

export abstract class FormMetaModel extends SmMetaModel {
  constructor(
    protected pageName: string,
    public override redirectPath: string,
    public redirectionUrl: string,
    protected numberOfRows: number,
    protected errorMessage: string = null,
    public override isStepForm: boolean = false,
    protected formName: string = '',
    protected extraClass: string = '',
    protected formRowClass: string = 'row align-items-start justify-content-center',
    protected isExtraActions: boolean = true,
    
  ) {
    super(pageName, redirectPath, isStepForm);
  }

  getMetaModel(): MetaModel {
    const pageInfo = new MetaModel();
    pageInfo.pageTitle = this.pageTitle;
    pageInfo.properties = this.initProperties();
    pageInfo.redirectPath = this.redirectPath;
    return pageInfo;
  }

  initProperties() {
    const formInfo = {
      isFormControl: false,
      numberOfRows: this.numberOfRows,
      errorMessage: this.errorMessage,
      isStepForm: this.isStepForm,
      formName: this.formName,
      extraClass: this.extraClass,
      formRowClass: this.formRowClass,
      isExtraActions: this.isExtraActions,
    };
    return formInfo;
  }
}

export class Headers {
  constructor(
    public readonly displayName: string,
    public readonly keyName: string,
    public readonly isSerchable: boolean,
    public readonly isSortable: boolean,
    public readonly feildType: string,
    public sortingOrder: SORTING_ORDER = '0',
    public NavigationDetails: navInfo = {
      isColumnNavigation: false,
      navigationUrl: ROUTE_PATH.dashboard, 
      navigateOperationType: OperationType.VIEW,
      navigateTransferData: {},
    },

    public readonly navigateToLink?: [string, string],
    public readonly toggle: toggleInfo = {
      isToggle: false,
      toggleKey: '',
      toggleCheckedValue: '',
      toggleUncheckedValue: '',
    },
    public readonly isVisible: boolean = true,
    public readonly showHeader: boolean = true
  ) {}
}

export class DetailEntity {
  constructor(
    public readonly keyName: string,
    public readonly feildName: string,
    public readonly style: string,
    public readonly isFileDownload: boolean = false,
    public readonly keyHeader: string = '',
    public readonly decisiveParam: any = { evalKey: '', evalParam: [] }
  ) {}
}

export abstract class SmUIModel {
  id: string;
  field_Name: string;
  fieldType: string;
  required: boolean;
  initClass: string;
  rowNumber: number;
  priority: number;
  label: string;
  type: string;
  custom: string;
  readOnly: boolean;
  isFormInput: boolean;
  isFormControl: boolean;
  isSwitchInput: boolean;
  placeholder: string;
  maxlength: number;
  minlength: number;
  isAutoComplete: boolean;
  requiredMsg: boolean;
  minValue: boolean;
  maxValue: boolean;
  pattern: boolean;
  isHidden: boolean;
  customError: boolean;
  noteMsg: string;
  condition: any;
  validate: boolean;
  validateParams: any[] = [];
  showprefix: boolean = false;
  prefixvalue: string = '';
  refreshValidator: any = null;

  constructor(
    protected propId: string = '',
    protected propfield_Name: string = '',
    protected propfieldType: string = '',
    protected proprequired: boolean = false,
    protected propInitClass: string = '',
    protected rowSize: number = 0,
    protected propPriority: number = 1,
    protected proplabel: string = '',
    protected proptype: string = '',
    protected propcustom: string = '',
    protected propreadOnly: boolean = false,
    protected propisFormInput: boolean = false,
    protected propisFormControl: boolean = false,
    protected propisSwitchInput: boolean = false,
    protected propmaxlength: number = 0,
    protected propminlength: number = 0,
    protected propisAutoComplete: boolean = false,
    protected propisHidden: boolean = false,
    protected proprequiredMsg: boolean = false,
    protected propminValue: boolean = false,
    protected propmaxValue: boolean = false,
    protected proppattern: boolean = false,
    protected propCustomError: boolean = false,
    protected propnoteMsg: string = '',
    protected propcondition: any = { evalKey: '', evalParam: [] },
    protected propValidate: boolean = false,
    protected propvalidateParams: any[] = [],
    protected propshowprefix: boolean = false,
    protected propprefixvalue: string = '',
    protected proprefreshValidator: any = null
  ) {
    this.getProperties();
  }

  getProperties(): void {
    this.id = this.propId;
    this.field_Name = this.propfield_Name;
    this.fieldType = this.propfieldType;
    this.required = this.proprequired;
    this.initClass = this.propInitClass;
    this.rowNumber = this.rowSize;
    this.priority = this.propPriority;
    this.label = this.proplabel;
    this.type = this.proptype;
    this.custom = this.propcustom;
    this.readOnly = this.propreadOnly;
    this.isFormInput = this.propisFormInput;
    this.isFormControl = this.propisFormControl;
    this.isAutoComplete = this.propisAutoComplete;
    this.isSwitchInput = this.propisSwitchInput;
    this.maxlength = this.propmaxlength;
    this.minlength = this.propminlength;
    this.isAutoComplete = this.propisAutoComplete;
    this.isHidden = this.propisHidden;
    this.requiredMsg = this.proprequiredMsg;
    this.customError = this.propCustomError;
    this.noteMsg = this.propnoteMsg;
    this.condition = this.propcondition;
    this.validate = this.propValidate;
    this.validateParams = this.propvalidateParams;
    this.showprefix = this.propshowprefix;
    this.prefixvalue = this.propprefixvalue;
    this.refreshValidator = this.proprefreshValidator;
  }
}

export class SmMetaInputModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected override propisHidden: boolean = false,
    protected override proprequiredMsg: boolean = false,
    protected override propminValue: boolean = false,
    protected override propmaxValue: boolean = false,
    protected override proppattern: boolean = false,
    protected override propCustomError: boolean = false,
    protected override propnoteMsg: string = '',
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override propValidate: boolean = false,
    protected override propvalidateParams: any[] = [],
    protected override propshowprefix: boolean = false,
    protected override propprefixvalue: string = '',
    protected override proprefreshValidator: any = null,
    protected propShowPassword: boolean = false,
    protected customAction: boolean = false,
    protected icon: boolean = false,
    public override placeholder: string = " ",
    protected isLabelVisible:boolean = true,
    protected infoicon: boolean = false,
    
    
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisSwitchInput,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propisHidden,
      proprequiredMsg,
      propminValue,
      propmaxValue,
      proppattern,
      propCustomError,
      propnoteMsg,
      propcondition,
      propValidate,
      propvalidateParams,
      propshowprefix,
      propprefixvalue,
      proprefreshValidator
    );
  }
}
export class SmMetaLabelModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput
    );
  }
}

export class SmMetaFileModel extends SmUIModel {
  fileKey: string;
  fileType: string;
  fileSize: number;
  imgHeight: number;
  imgWidth: number;
  FixedResolution: boolean;
  maximgHeight: number;
  maximgWidth: number;
  downloadkeyword: string = '';
  filePathKey: string = 'filePath';
  // noGif: boolean = false;
  override condition: any = null;
  isResetFile: boolean = false;
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = 'singleimagepreview' ||
      'multipleimagepreview' ||
      'PDF' ||
      'multitypeimagepreview',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propFileType: any = '',
    protected propFileSize: number = 0,
    protected propImgHeigth: number = 0,
    protected propImgWidth: number = 0,
    protected propfileKey: any = '',
    protected propfilePathKey: string = 'filePath',
    protected propIsHidden: boolean = false,
    protected propFixedResolution: boolean = true,
    protected propmaxImgHeight: number = 0,
    protected propmaxImgWidth: number = 0,
    protected propdownloadkeyword: string = '',
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected isReset: boolean = false,
    protected noGif: boolean = false
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propIsHidden,
      propcondition
    );
  }

  override getProperties(): void {
    super.getProperties();
    this.fileType = this.propFileType;
    this.fileSize = this.propFileSize;
    this.imgHeight = this.propImgHeigth;
    this.imgWidth = this.propImgWidth;
    this.fileKey = this.propfileKey;
    this.FixedResolution = this.propFixedResolution;
    this.maximgHeight = this.propmaxImgHeight;
    this.maximgWidth = this.propmaxImgWidth;
    this.downloadkeyword = this.propdownloadkeyword;
    this.filePathKey = this.propfilePathKey;
    this.condition = this.propcondition;
    this.isResetFile = this.isReset;
  }
}

export class SmCheckboxModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propIsHidden: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override proprefreshValidator: any = null
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propcondition,
      propIsHidden,
      proprefreshValidator
    );
  }
}

export class SmToggleModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propIsHidden: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override proprefreshValidator: any = null
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propcondition,
      propIsHidden,
      proprefreshValidator
    );
  }
}

export class SmRadioModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propIsHidden: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override proprefreshValidator: any = null,
    protected optionsNumber: number = 0,
    protected optionsArray: any[] = []
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propcondition,
      propIsHidden,
      proprefreshValidator
    );
  }
}
export class SmDatepickerModel extends SmUIModel {
  format: string;
  minDate: any;
  maxDate: any;
  daysDisabled: any;
  maxDateToday: boolean;
  isPlaceholder:any;
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propisAutoComplete: boolean = false,
    protected propFormat: string = '',
    protected propminDate: any = '',
    protected propmaxDate: any = '',
    protected propdaysDisabled: any = [],
    protected propIsHidden: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected propmaxDateToday: boolean = false,
    protected propisPlaceholder:any={ isPlaceholder: false, placeholderLabel:'',isIconVisible:true,isLabel:true }
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propcondition
    );
  }

  override getProperties(): void {
    super.getProperties();
    this.format = this.propFormat;
    this.minDate = this.propminDate;
    this.maxDate = this.propmaxDate;
    this.daysDisabled = this.propdaysDisabled;
    this.maxDateToday = this.propmaxDateToday;
    this.isPlaceholder = this.propisPlaceholder;
  }
}

export class SmMetaTextAreaModel extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propIsHidden: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    public override placeholder: string = " "
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propIsHidden,
      propcondition
    );
  }
}

export class SmMetaSelectModel extends SmUIModel {
  options: any[] = [];
  bindLabel: string;
  bindValue: string;
  isMultiSelect: boolean;
  isGroup: boolean;
  isTextArea: boolean;
  isSelectAll: boolean;
  isInput: boolean;
  originalOptions: any[] = [];
  isFilter: boolean = false;
  filterOptions: filterCheckboxModel[] = [];
  isBlueOnChange: boolean = false;

  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propOptions: any[] = [],
    protected propBindLabel: string = 'label',
    protected propBindValue: string = 'value',
    protected propIsMultiSelect: boolean = false,
    protected propIsGroup: boolean = false,
    protected propIsHidden: boolean = false,
    protected propisTextArea: boolean = false,
    protected propisSelectAll: boolean = false,
    protected propisInput: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override proprefreshValidator: any = null,
    protected propIsFilter: boolean = false,
    protected propFilterOptions: filterCheckboxModel[] = [],
    protected propIsBlueOnChange: boolean = false,
    protected showLabel:boolean = true,
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propIsHidden,
      propcondition,
      proprefreshValidator,
      propIsBlueOnChange
    );
    this.getProperties();
  }

  override getProperties(): void {
    super.getProperties();
    this.options = this.propOptions;
    this.bindLabel = this.propBindLabel;
    this.bindValue = this.propBindValue;
    this.isMultiSelect = this.propIsMultiSelect;
    this.isGroup = this.propIsGroup;
    this.isTextArea = this.propisTextArea;
    this.isSelectAll = this.propisSelectAll;
    this.isInput = this.propisInput;
    this.originalOptions = this.options;
    this.filterOptions = this.propFilterOptions;
    this.isBlueOnChange = this.propIsBlueOnChange;
  }
}

export class SmMetaMultipleChoiceModel extends SmUIModel {
  options: any[] = [];
  bindLabel: string;
  bindValue: string;
  isMultiSelect: boolean;
  isGroup: boolean;
  isTextArea: boolean;
  isSelectAll: boolean;
  isInput: boolean;
  originalOptions: any[] = [];
  isFilter: boolean = false;
  filterOptions: filterCheckboxModel[] = [];

  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proplabel: string = '',
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propmaxlength: number = 0,
    protected override propminlength: number = 0,
    protected override propisAutoComplete: boolean = false,
    protected propOptions: multipleChoiceOption[] = [],
    protected propBindLabel: string = 'label',
    protected propBindValue: string = 'value',
    protected propIsMultiSelect: boolean = false,
    protected propIsGroup: boolean = false,
    protected propIsHidden: boolean = false,
    protected propisTextArea: boolean = false,
    protected propisSelectAll: boolean = false,
    protected propisInput: boolean = false,
    protected override propcondition: any = { evalKey: '', evalParam: [] },
    protected override proprefreshValidator: any = null,
    protected propIsFilter: boolean = false,
    protected propFilterOptions: filterCheckboxModel[] = []
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete,
      propmaxlength,
      propminlength,
      propisAutoComplete,
      propIsHidden,
      propcondition,
      proprefreshValidator
    );
    this.getProperties();
  }

  override getProperties(): void {
    super.getProperties();
    this.options = this.propOptions;
    this.bindLabel = this.propBindLabel;
    this.bindValue = this.propBindValue;
    this.isMultiSelect = this.propIsMultiSelect;
    this.isGroup = this.propIsGroup;
    this.isTextArea = this.propisTextArea;
    this.isSelectAll = this.propisSelectAll;
    this.isInput = this.propisInput;
    this.originalOptions = this.options;
    this.filterOptions = this.propFilterOptions;
  }
}

export class SmMetaColourPicker extends SmUIModel {
  constructor(
    protected override propId: string = '',
    protected override propfield_Name: string = '',
    protected override propfieldType: string = '',
    protected override proprequired: boolean = false,
    protected override proplabel: string = '',
    protected override propInitClass: string = '',
    protected override rowSize: number,
    protected override propPriority: number,
    protected override proptype: string = '',
    protected override propcustom: string = '',
    protected override propreadOnly: boolean = false,
    protected override propisFormInput: boolean = false,
    protected override propisFormControl: boolean = false,
    protected override propisSwitchInput: boolean = false,
    protected override propisAutoComplete: boolean = false,
    protected propIsHidden: boolean = false
  ) {
    super(
      propId,
      propfield_Name,
      propfieldType,
      proprequired,
      propInitClass,
      rowSize,
      propPriority,
      proplabel,
      proptype,
      propcustom,
      propreadOnly,
      propisFormInput,
      propisFormControl,
      propisAutoComplete
    );
  }
}

export class SmBtnMetaModel extends SmUIModel {
  // type:primary, secondary, light, dark, success, danger, warning, info, link
  isSubmit: boolean;
  isError: boolean;
  show: boolean;
  override readOnly: boolean;

  constructor(
    protected override proplabel: string,
    protected override proptype: string,
    protected propIsSubmit: boolean,
    protected propisError: boolean,
    protected propshow: boolean,
    protected propreadonly: boolean = false,
    protected isHideButton : boolean = false

  ) {
    super();
    this.getProperties();
  }

  override getProperties(): void {
    this.label = this.proplabel;
    this.type = this.proptype;
    this.isSubmit = this.propIsSubmit;
    this.isError = this.propisError;
    this.show = this.propshow;
    this.readOnly = this.propreadOnly;
    this.isHideButton = this.isHideButton

  }
}

export class SmMetaDynamicFormModel {
  id: string;
  field_Name: string;
  fieldType: string;
  isVisible: boolean;
  required: boolean;
  initClass: string;
  rowNumber: number;
  priority: number;
  label: string;
  formtype: string = 'addfirst' || 'addlast';
  fields: any;
  fieldrowCount: number = 4;
  isDelete: boolean = true;
  isAdd: boolean = true;

  isFormInput: boolean;
  isFormControl: boolean;
  extraNullFields: any = {};
  maxrows: number = 4;
  isExtraValidation: boolean = true;
  condition: any;

  constructor(
    protected propid: any,
    protected propfieldName: string,
    protected propfieldType: string,
    protected propIsVisible: boolean,
    protected proprequired: boolean,
    protected propinitClass: string,
    protected proprowNumber: number,
    protected propPriority: number,
    protected proplabel: string,
    protected propformtype: string,
    protected propfields: any,
    protected propfieldrowcount: number = 4,
    protected propisDelete: boolean = true,
    protected propisAdd: boolean = true,
    protected propisFormInput: boolean = true,
    protected propisFormControl: boolean = true,
    protected propextraNullFields: any = {},
    protected propmaxrows: number = 4,
    protected propisExtraValidation: boolean = true,
    protected propcondition: any = { evalKey: '', evalParam: [] }
  ) {
    this.getProperties();
  }

  getProperties(): void {
    this.id = this.propid;

    this.field_Name = this.propfieldName;
    this.fieldType = this.propfieldType;
    this.isVisible = this.propIsVisible;
    this.required = this.proprequired;
    this.initClass = this.propinitClass;
    this.rowNumber = this.proprowNumber;
    this.priority = this.propPriority;
    this.label = this.proplabel;
    this.formtype = this.propformtype;
    this.fields = this.propfields;
    this.fieldrowCount = this.propfieldrowcount;
    this.isDelete = this.propisDelete;
    this.isAdd = this.propisAdd;
    this.isFormInput = this.propisFormInput;
    this.isFormControl = this.propisFormControl;
    this.extraNullFields = this.propextraNullFields;
    this.maxrows = this.propmaxrows;
    this.isExtraValidation = this.propisExtraValidation;
    this.condition = this.propcondition;
  }
}
// export class LabelMetaModel extends SmUIModel {
//     override id: string;
//     override label: string;

//     constructor(
//         protected propid: string,
//         protected propLabel: string,
//     ) {
//         super();
//         this.getProperties();
//     }

//     override getProperties(): void {
//         this.id = this.propid;
//         this.label = this.propLabel;
//     }
// }
